import { useEffect, useState } from 'react'

import { Button } from '@/shared/ui/Button'

import { getPhase, MAX_RETRIES, retryDelayMs } from './retry'

/**
 * Degradação progressiva quando o evento não carrega.
 *
 * Substitui o overlay de tela cheia do `GlobalLoader`. O comportamento é o
 * mesmo — os cinco patamares, o backoff, o contador, o teto e as duas ações;
 * o que muda é quanto espaço cada patamar ocupa.
 *
 * Antes, a primeira falha já tomava a tela inteira. Falha única em rede
 * instável é comum e transitória, e tratar as duas pontas do espectro com a
 * mesma severidade ensina o usuário a ignorar o aviso. Aqui a faixa cresce com
 * a insistência do problema, e só o caso terminal — dez tentativas, nada a
 * mostrar — ocupa o conteúdo.
 */

const useCountdown = (failureCount: number): number => {
  const [secondsLeft, setSecondsLeft] = useState(0)

  useEffect(() => {
    if (failureCount === 0) {
      setSecondsLeft(0)
      return undefined
    }
    const deadline = Date.now() + retryDelayMs(failureCount)
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)))
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [failureCount])

  return secondsLeft
}

export type BootFailureNoticeProps = {
  failureCount?: number
  /**
   * Falha que não vai se resolver sozinha — a resposta do evento veio com
   * status de erro. Escala direto para o patamar terminal em vez de esperar
   * dez tentativas que não vão acontecer: não há retentativa automática para
   * um 403.
   */
  terminal?: boolean
  onRetry?: () => void
}

export const BootFailureNotice = ({
  failureCount = 0,
  terminal = false,
  onRetry
}: BootFailureNoticeProps) => {
  const phase = terminal ? 4 : getPhase(failureCount)
  const secondsLeft = useCountdown(terminal ? 0 : failureCount)

  if (phase === 0) return null

  const contagem =
    secondsLeft > 0 ? `Nova tentativa em ${secondsLeft}s` : 'Tentando...'
  const tentativa = `Tentativa ${failureCount} de ${MAX_RETRIES} · ${contagem}`

  // Fase 4: dez tentativas falharam e não há o que mostrar por baixo. É o
  // único patamar que substitui o conteúdo — aqui o espaço é para as ações.
  if (phase === 4) {
    return (
      <div
        role="alert"
        data-testid="boot-failure"
        data-phase={phase}
        className="fixed inset-x-0 bottom-bottomnav top-topbar z-drawer flex flex-col items-center justify-center gap-3 bg-[var(--color-body-bg)] px-8 text-center"
      >
        <p className="text-base font-semibold text-gray-900">
          Não foi possível carregar sua experiência.
        </p>
        <p className="text-sm text-gray-700">
          Continuaremos tentando em segundo plano.
        </p>
        <div className="mt-2 flex w-full max-w-xs flex-col gap-2">
          {onRetry && (
            <Button variant="primary" size="sm" onClick={onRetry}>
              Tentar agora
            </Button>
          )}
          <Button
            variant="light"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Recarregar página
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="status"
      data-testid="boot-failure"
      data-phase={phase}
      className="fixed inset-x-0 top-[calc(env(safe-area-inset-top)+2px)] z-drawer mx-auto flex w-full max-w-2xl flex-col items-center gap-1.5 px-4 py-2 text-center"
    >
      <div className="flex w-full flex-col items-center gap-1.5 rounded-[10px] border-2 border-solid border-contour bg-white px-3 py-2 shadow-sm">
        <p className="text-sm leading-snug text-gray-900">
          {phase === 1 && 'Estamos com dificuldade para carregar.'}
          {phase === 2 &&
            'Ainda não conseguimos conectar. Pode ser instabilidade na sua internet ou no servidor.'}
          {phase === 3 &&
            'Continuamos tentando automaticamente. Você não precisa fazer nada.'}
        </p>

        <p className="text-2xs text-gray-700">
          {phase === 1 ? contagem : tentativa}
        </p>

        {phase === 3 && onRetry && (
          <Button
            variant="primary-ghost"
            size="sm"
            showTrailingIcon={false}
            onClick={onRetry}
          >
            Tentar agora
          </Button>
        )}
      </div>
    </div>
  )
}

export { getPhase, MAX_RETRIES, retryDelayMs } from './retry'
